import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { CartResponse } from './cart.types';

type UseDeleteCartItemResponse = {
  cart_item: {
    uuid: string;
  };
};

type UseDeleteCartItemRequest = {
  causeId: string;
  cartId: string;
  cartItemId: string;
  /** Key to use for invalidation: Optional on the donor side  */
  queryKeyId?: string;
};

const useDeleteCartItem = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const deleteStoreItem = async ({ cartId, cartItemId }: UseDeleteCartItemRequest) => {
    const { data } = await StoreApi.delete<UseDeleteCartItemResponse>(
      `carts/${cartId}/items/${cartItemId}`
    );
    return data;
  };

  return useMutation({
    mutationFn: deleteStoreItem,
    onSuccess(_, variables) {
      const { queryKeyId = 'user', cartId } = variables;
      // We can tie an update to either the causeId, or 'user' if we don't have a causeId
      const invalidationKey = storeKeys.id(queryKeyId).carts.id(cartId).all;

      void queryClient.invalidateQueries({ queryKey: invalidationKey });
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(queryKeyId).consumer.checkout.all,
      });
    },
  });
};

type RefundCartDonationParams = {
  cartId?: string;
  causeId?: string;
  /** Providing cartItemIds will allow a partial refund of a cart */
  cartItemIds?: string[];
};

const useRefundCart = () => {
  const StoreApi = useStoreApi();

  const refundCartDonation = async ({ cartId, causeId, cartItemIds }: RefundCartDonationParams) => {
    const partialRefundQueryParamString = cartItemIds?.join(',') ?? '';

    const params = new URLSearchParams();
    if (causeId) params.set('cause_id', causeId);
    if (cartItemIds?.length) params.set('item_id', partialRefundQueryParamString);

    const { data } = await StoreApi.delete<CartResponse>(`/carts/${cartId}?${params.toString()}`, {
      data: {
        cause_id: causeId,
        uuid: cartId,
      },
    });
    return data;
  };

  return useMutation({
    mutationFn: refundCartDonation,
    mutationKey: ['carts', 'refund'],
  });
};

export { useDeleteCartItem, useRefundCart };
